import { Inject, Injectable, signal } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { DOCUMENT } from '@angular/common';
import { Observable, of, BehaviorSubject, throwError } from 'rxjs';
import { catchError, tap } from 'rxjs/operators';
import { CacheService, CacheEntry } from './cache.service';
import { SmilyContext } from '../../../openapi-ts/model/smilyContext';

import {
  Loan,
  FilterOptions,
  Result,
  SortOptions,
  OffersOfApplication,
  Agent,
} from '../type/result_type';

@Injectable({
  providedIn: 'root',
})
export class ResultpageService {
  readonly smilyContext = signal<SmilyContext | null>(null);

  //--- load data for communication between components---
  private loanStateData$ = new BehaviorSubject<any>(1);
  loanStateDataObserv$ = this.loanStateData$.asObservable();
  //--- load data for communication between components---
  //---for updating local loan data---
  updateStateLoanData(data: Loan) {
    this.loanStateData$.next(data);
  }
  //---for updating local loan data---

  //--- load data for sending post request---
  private loanDataPost$ = new BehaviorSubject<any>(1);
  loanDataPostObserv$ = this.loanDataPost$.asObservable();
  //--- load data for sending post request---
  //---for updating local loan data for post request---
  updatedLoanDataPost(data: any) {
    this.loanDataPost$.next(data);
  }
  //---for updating local loan data for post request---

  //--- filterState data for communication between components---
  private filterStateData$ = new BehaviorSubject<any>(1);
  filterStateDataObserv$ = this.filterStateData$.asObservable();
  //--- filterState data for communication between components---
  //---for updating local filterState data---
  updatedStateFilerData(data: any) {
    this.filterStateData$.next(data);
  }
  //---for updating local filterState data---

  //--- filter data for post request---
  private filterDataPost$ = new BehaviorSubject<any>(1);
  filterDataPostObserv$ = this.filterDataPost$.asObservable();
  //--- filter data for post request---
  //---for updating local filter data for post---
  updatedFilerDataPost(data: any) {
    this.filterDataPost$.next(data);
  }
  //---for updating local filter data for post---

  //--- filterOption data for communication between components---
  private filerOptionsData$ = new BehaviorSubject<any>(1);
  filerOptionsDataObserv$ = this.filerOptionsData$.asObservable();
  //--- filterOption data for communication between components---
  //---local storage for updating filer's options data---
  updateFilerOptionsData(data: FilterOptions | number) {
    this.filerOptionsData$.next(data);
  }
  //---local storage for updating filer's options data---

  //---offers data for communication between components---
  private offersData$ = new BehaviorSubject<any>(1);
  offersDataObserv$ = this.offersData$.asObservable();
  //---offers data for communication between components---
  //---local storage for updating filer's options data---
  updateOffersData(data: OffersOfApplication[]) {
    this.offersData$.next(data);
  }

  updateSmilyContext(smily?: SmilyContext): void {
    const displayState = smily?.displayState;
    if (
      !displayState ||
      displayState === SmilyContext.DisplayStateEnum.LoginRegisterNeeded
    ) {
      this.smilyContext.set(null);
      return;
    }
    this.smilyContext.set(smily);
  }

  getOffersDataSnapshot(): OffersOfApplication[] | null {
    const value = this.offersData$.getValue();
    return Array.isArray(value) ? value : null;
  }
  //---local storage for updating filer's options data---

  //---sorting item data for communication between components---
  private sortingSubject$ = new BehaviorSubject<any>(1);
  optionSortingObserv$ = this.sortingSubject$.asObservable();
  //---sorting item data for communication between components---
  //---local storage for exchanging data sorting's option---
  updatedDataSorting(optionSorting: SortOptions) {
    this.sortingSubject$.next(optionSorting);
  }
  //---local storage for exchanging data sorting's option---

  //---show overlay when data load---
  private overlay$ = new BehaviorSubject<any>(false);
  overlayObserv$ = this.overlay$.asObservable();
  //---show overlay when data load---
  //---updating overlay---
  updatedOverlay(show: boolean) {
    this.overlay$.next(show);
  }
  //---updating overlay---

  //---local storage for offer's elements---
  private offers$ = new BehaviorSubject<any>(1);
  offersObserv$ = this.offers$.asObservable();
  //---local storage for offer's elements---
  //---updating local storage for offer---
  updatedCards(card: boolean) {
    this.offers$.next(card);
  }
  //---updating local storage for offer---

  //---disable loan button---
  // private disableLoanButton$ = new BehaviorSubject<boolean>(true);
  // disableLoanButtonObserv$ = this.disableLoanButton$.asObservable();
  // updatedDisableLoanButton(value: boolean) {
  //   this.disableLoanButton$.next(value);
  // }
  //---disable loan button---

  private localtUrl = '/resultpage/assets/data/data.json';
  private location = '/api/resultpage/';
  private token =
    'G62R02h-6lwPP4EQMgHbO_-9YJq3mKf-3T2PgMwfGqfT6URSCxhzVIlbKIPHIlSrVJj6uBdDfTU%3D';
  private url = this.location + this.token;

  httpOptions = {
    headers: new HttpHeaders({
      'Content-Type': 'application/json',
      'api-key': 'check24at',
      'Cache-Control': 'max-age=2592000',
      'x-from-service': 'frontend-resultpage',
    }),
  };

  private agentDataCache = new Map<string, CacheEntry<Agent>>();

  constructor(
    private http: HttpClient,
    @Inject(DOCUMENT) private document: Document,
    private cacheService: CacheService,
  ) {}

  getResult(token: any): Observable<Result> {
    const url = `${this.location}${encodeURIComponent(token)}?deviceoutput=${window['wf_deviceoutput']}`;
    return this.http
      .get<Result>(url, this.httpOptions)
      .pipe(catchError(this.handleError<Result>('getResultPage')));
  }

  postFilter(token: any, obj: any): Observable<Result> {
    const url = `${this.location}${encodeURIComponent(token)}?deviceoutput=${window['wf_deviceoutput']}`;
    return this.http
      .post<Result>(url, obj, this.httpOptions)
      .pipe(catchError(this.handleError<Result>('postFilter')));
  }

  getLoanAlreadyRequested(token: any): Observable<Loan[]> {
    let url = this.location + encodeURIComponent(token) + '/alreadyRequested';
    return this.http
      .get<Loan[]>(url, this.httpOptions)
      .pipe(catchError(this.handleError<Loan[]>('postFilter')));
  }

  postLoan(token: any): Observable<Result> {
    let url = this.location + encodeURIComponent(token) + '/newComparison';
    let obj: any = {};
    this.loanDataPostObserv$.subscribe((data) => (obj = data));
    return this.http
      .post<Result>(url, obj, this.httpOptions)
      .pipe(catchError(this.handleError<Result>('postFilter')));
  }

  //---get data for the agent component---
  getAgentData(token: string): Observable<Agent> {
    let url = this.location + encodeURIComponent(token) + '/agent';
    let obj: any = {};
    return this.http
      .post<Agent>(url, obj, this.httpOptions)
      .pipe(catchError(this.handleError<Agent>('getAgentData')));
  }
  //---get data for the agent component---

  //--- Eternal page API methods ---
  private eternalLocation = '/api/customerpage';

  checkLogin(): Observable<any> {
    let url = this.eternalLocation + '/checkSession';
    return this.http.get<any>(url, this.httpOptions)
      .pipe(
        catchError(this.handleError<any>('checkLogin'))
      );
  }

  getAgentDataForOffer(offerId: string): Observable<Agent> {
    const cacheKey = `agent:${offerId}`;

    // Return cached agent data if valid
    if (this.cacheService.isValid(this.agentDataCache, cacheKey)) {
      return of(this.cacheService.get(this.agentDataCache, cacheKey)!);
    }

    let url = this.eternalLocation + '/agent-for-offer'+'?offerId='+ offerId;
    return this.http.get<Agent>(url, this.httpOptions)
      .pipe(
        tap((data: Agent) => {
          this.cacheService.set(this.agentDataCache, cacheKey, data);
        }),
        catchError(this.handleError<Agent>('getAgentDataForOffer'))
      );
  }

  getKreditcenterData(): Observable<Result> {
    const url = this.eternalLocation;
    return this.http.get<Result>(url, this.httpOptions)
      .pipe(
        catchError(this.handleError<Result>('getKreditcenterData'))
      );
  }

  postEternalFilter(obj: any): Observable<Result> {
    const url = this.eternalLocation;
    return this.http.post<Result>(url, obj, this.httpOptions)
      .pipe(
        catchError(this.handleError<Result>('postEternalFilter'))
      );
  }

  getKreditcenterCrm(token: string): Observable<Result> {
    const url = `${this.eternalLocation}?token=${token}`;
    return this.http.get<Result>(url, this.httpOptions)
      .pipe(
        catchError(this.handleError<Result>('getKreditcenterCrm'))
      );
  }

  getActiveData(): Observable<Result> {
    const url = `${this.eternalLocation}/active`;
    return this.http.get<Result>(url, this.httpOptions)
      .pipe(
        catchError(this.handleError<Result>('getActiveData'))
      );
  }

  postActiveFilter(obj: any): Observable<Result> {
    const url = `${this.eternalLocation}/active`;
    return this.http.post<Result>(url, obj, this.httpOptions)
      .pipe(
        catchError(this.handleError<Result>('postActiveFilter'))
      );
  }

  getArchiveData(): Observable<Result> {
    const url = `${this.eternalLocation}/archive`;
    return this.http.get<Result>(url, this.httpOptions)
      .pipe(
        catchError(this.handleError<Result>('getArchiveData'))
      );
  }

  postArchiveFilter(obj: any): Observable<Result> {
    const url = `${this.eternalLocation}/archive`;
    return this.http.post<Result>(url, obj, this.httpOptions)
      .pipe(
        catchError(this.handleError<Result>('postArchiveFilter'))
      );
  }
  //--- End Eternal page API methods ---

  private handleError<T>(operation = 'operation', result?: T) {
    return (error: any): Observable<T> => {
      // TODO: send the error to remote logging infrastructure
      console.error(error); // log to console instead

      // Let the app keep running by returning an empty result.
      return throwError(() => {
        return error;
      });
    };
  }
}
